import { useState } from "react";
import { MapPin, Hexagon, X, Check, Trash2, Circle as CircleIcon, ChevronLeft, Spline, Upload } from "lucide-react";

export const POI_CAT_OPTS = [
  { value: "client", label: "Client" },
  { value: "depot", label: "Dépôt" },
  { value: "station", label: "Station" },
  { value: "fournisseur", label: "Fournisseur" },
  { value: "autre", label: "Autre" },
];

const COLORS = ["#17A2B8", "#F5821F", "#10B981", "#8B5CF6", "#EF4444", "#0EA5E9"];

export default function PoiPanel({
  poiDrawMode, poiDraft, poiFilters, setPoiFilters,
  onStartPoint, onStartPolygon, onStartTroncon, onCancelDraw, onFinishPolygon, onFinishTroncon,
  poiForm, setPoiForm, onSave, onDelete, companies = [], companyFilter, requireCompany = false,
  showImport = false, onImportClick, canManage = true,
}) {
  const drawing = !!poiDrawMode;
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Toolbar POI — reduit et reduisable (édition/gestion : permission pois_manage) */}
      {canManage && <div className="absolute top-3 left-3 lg:left-[352px] z-[540] hidden md:block animate-slidein" data-testid="poi-toolbar">
        {open ? (
          <div className="glass rounded-lg p-2 flex items-center gap-1.5">
            <span className="label-mono mr-1">POI</span>
            <button type="button" onClick={onStartPoint} data-testid="poi-add-point"
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${poiDrawMode === "point" ? "border-[#17A2B8] text-[#2ec5db] bg-[#17A2B8]/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <CircleIcon className="w-3.5 h-3.5" /> Cercle
            </button>
            <button type="button" onClick={onStartPolygon} data-testid="poi-add-polygon"
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${poiDrawMode === "polygon" ? "border-[#17A2B8] text-[#2ec5db] bg-[#17A2B8]/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <Hexagon className="w-3.5 h-3.5" /> Polygone
            </button>
            <button type="button" onClick={onStartTroncon} data-testid="poi-add-troncon"
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${poiDrawMode === "troncon" ? "border-[#F5821F] text-[#F5821F] bg-[#F5821F]/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <Spline className="w-3.5 h-3.5" /> Tronçon
            </button>
            {showImport && (
              <button type="button" onClick={onImportClick} data-testid="poi-import-map-btn" title="Importer des POI (CSV/Excel)"
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border border-border text-muted-foreground hover:text-foreground transition-colors">
                <Upload className="w-3.5 h-3.5" /> Importer
              </button>
            )}
            <button type="button" onClick={() => setOpen(false)} data-testid="poi-collapse" title="Réduire"
              className="ml-0.5 text-muted-foreground hover:text-foreground p-1 rounded hover:bg-black/5"><ChevronLeft className="w-4 h-4" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => setOpen(true)} data-testid="poi-expand" title="Ajouter un POI"
            className="glass rounded-full w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-[#17A2B8] transition-colors">
            <MapPin className="w-5 h-5" />
          </button>
        )}
      </div>}

      {/* Draw hint / polygon controls */}
      {drawing && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 mt-16 lg:mt-14 z-[560] glass rounded-lg px-3 py-2 flex items-center gap-3 animate-slidein" data-testid="poi-draw-bar">
          <span className="text-xs font-semibold">
            {poiDrawMode === "point" ? "Cliquez sur la carte pour placer le POI" : poiDrawMode === "troncon" ? `Cliquez le long de la route (${poiDraft?.points?.length || 0} pts)` : `Cliquez pour ajouter des sommets (${poiDraft?.coordinates?.length || 0})`}
          </span>
          {poiDrawMode === "polygon" && (
            <button type="button" onClick={onFinishPolygon} data-testid="poi-finish-polygon"
              className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-2.5 py-1 rounded-md text-xs">
              <Check className="w-3.5 h-3.5" /> Terminer
            </button>
          )}
          {poiDrawMode === "troncon" && (
            <button type="button" onClick={onFinishTroncon} data-testid="poi-finish-troncon"
              className="flex items-center gap-1 bg-[#F5821F] hover:bg-[#e0741a] text-white font-semibold px-2.5 py-1 rounded-md text-xs">
              <Check className="w-3.5 h-3.5" /> Terminer
            </button>
          )}
          <button type="button" onClick={onCancelDraw} data-testid="poi-cancel-draw"
            className="flex items-center gap-1 text-[#EF4444] text-xs font-semibold"><X className="w-3.5 h-3.5" /> Annuler</button>
        </div>
      )}

      {/* Create / edit form (permission pois_manage) */}
      {canManage && poiForm && (
        <div
          className={poiForm.type === "troncon"
            ? "fixed bottom-4 left-1/2 -translate-x-1/2 z-[2000] w-[340px] max-w-[92vw] animate-slidein"
            : "fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"}
          onClick={poiForm.type === "troncon" ? undefined : () => setPoiForm(null)}>
          <div
            className={poiForm.type === "troncon"
              ? "glass rounded-lg p-5 shadow-2xl border border-[#F5821F]/50"
              : "glass rounded-lg w-full max-w-md p-6 animate-slidein"}
            onClick={(e) => e.stopPropagation()} data-testid="poi-form">
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-lg flex items-center gap-2">
                {poiForm.type === "polygon" ? <Hexagon className="w-5 h-5 brand-teal" /> : poiForm.type === "troncon" ? <Spline className="w-5 h-5" style={{ color: "#F5821F" }} /> : <MapPin className="w-5 h-5 brand-teal" />}
                {poiForm.id ? "Modifier le POI" : poiForm.type === "troncon" ? "Nouveau tronçon" : "Nouveau POI"}
              </p>
              <button onClick={() => setPoiForm(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label-mono block mb-1">Nom</label>
                <input data-testid="poi-name" autoFocus value={poiForm.name}
                  onChange={(e) => setPoiForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1">Catégorie</label>
                  <select data-testid="poi-category" value={poiForm.category}
                    onChange={(e) => setPoiForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]">
                    {POI_CAT_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1">Compagnie{requireCompany ? " *" : ""}</label>
                  <select data-testid="poi-company" value={poiForm.company_id || ""}
                    onChange={(e) => setPoiForm((f) => ({ ...f, company_id: e.target.value }))}
                    className="w-full bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]">
                    {requireCompany
                      ? <option value="" disabled>— Choisir une compagnie —</option>
                      : <option value="">— Aucune (global) —</option>}
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="label-mono block mb-1">Couleur</label>
                <div className="flex items-center gap-2">
                  {COLORS.map((c) => (
                    <button key={c} type="button" data-testid={`poi-color-${c.replace("#", "")}`}
                      onClick={() => setPoiForm((f) => ({ ...f, color: c }))}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${poiForm.color === c ? "scale-110 border-foreground" : "border-transparent"}`}
                      style={{ background: c }} />
                  ))}
                </div>
              </div>

              {poiForm.type === "point" && (
                <div>
                  <label className="label-mono block mb-1">Rayon de zone (m) — optionnel</label>
                  <input data-testid="poi-radius" type="number" min="0" value={poiForm.radius ?? ""}
                    onChange={(e) => setPoiForm((f) => ({ ...f, radius: e.target.value === "" ? null : Number(e.target.value) }))}
                    placeholder="0 = aucun cercle"
                    className="w-full bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]" />
                </div>
              )}

              {poiForm.type === "troncon" && (
                <div>
                  <label className="label-mono block mb-1">Largeur du couloir : {poiForm.width_m ?? 30} m</label>
                  <input data-testid="poi-width" type="range" min="5" max="200" step="5" value={poiForm.width_m ?? 30}
                    onChange={(e) => setPoiForm((f) => ({ ...f, width_m: Number(e.target.value) }))}
                    className="w-full accent-[#F5821F]" />
                </div>
              )}

              <div>
                <label className="label-mono block mb-1">Note</label>
                <textarea data-testid="poi-note" rows={2} value={poiForm.note || ""}
                  onChange={(e) => setPoiForm((f) => ({ ...f, note: e.target.value }))}
                  className="w-full bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]" />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-5">
              <button onClick={onSave} data-testid="poi-save"
                className="flex-1 flex items-center justify-center gap-1.5 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold py-2 rounded-md text-sm">
                <Check className="w-4 h-4" /> Enregistrer
              </button>
              {poiForm.id && (
                <button onClick={onDelete} data-testid="poi-delete"
                  className="flex items-center justify-center gap-1.5 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 border border-[#EF4444]/40 text-[#EF4444] font-semibold px-4 py-2 rounded-md text-sm">
                  <Trash2 className="w-4 h-4" /> Supprimer
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
